'use client';

import { Box, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export type StatusPageSeverity = 'success' | 'error' | 'warning' | 'info';

export interface StatusPageProps {
  /** Severity determines the icon color */
  severity: StatusPageSeverity;
  /** Large icon displayed at the top */
  icon: ReactNode;
  /** Page title */
  title: string;
  /** Primary description text */
  description?: string;
  /** Optional detail content (e.g., GlassAlert, additional info) rendered between description and actions */
  detail?: ReactNode;
  /** Action buttons rendered at the bottom */
  action?: ReactNode;
}

const severityColorMap: Record<StatusPageSeverity, string> = {
  success: 'success.main',
  error: 'error.main',
  warning: 'warning.main',
  info: 'info.main',
};

/**
 * StatusPage Component
 *
 * Full-page status display for review states, confirmations, and status messages.
 * Unlike EmptyState (for "no data" scenarios), StatusPage is for actionable
 * status communication: pending review, account suspended, verification sent, etc.
 *
 * Usage:
 * ```tsx
 * <StatusPage
 *   severity="info"
 *   icon={<AccessTimeIcon sx={{ fontSize: 64 }} />}
 *   title="Registration Under Review"
 *   description="Your registration has been submitted for review."
 *   detail={<GlassAlert severity="info" visible>Details here</GlassAlert>}
 *   action={<GlassButton variant="outlined">Back to Login</GlassButton>}
 * />
 * ```
 */
export const StatusPage = ({
  severity,
  icon,
  title,
  description,
  detail,
  action,
}: StatusPageProps): React.ReactElement => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box sx={{ mb: 2, color: severityColorMap[severity] }}>{icon}</Box>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            mb: 3,
            maxWidth: 480,
            mx: 'auto',
          }}
        >
          {description}
        </Typography>
      )}
      {detail && <Box sx={{ mb: 3, maxWidth: 480, width: '100%' }}>{detail}</Box>}
      {action && <Box>{action}</Box>}
    </Box>
  );
};

export default StatusPage;
